import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Link, Navigate } from "react-router-dom";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Location from "@/pages/Location";
import "@/App.css";

const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

const Portfolio = lazy(() => import("@/pages/Portfolio"));
const Investment = lazy(() => import("@/pages/Investment"));
const About = lazy(() => import("@/pages/About"));
const KindWords = lazy(() => import("@/pages/KindWords"));
const Contact = lazy(() => import("@/pages/Contact"));
const Wedding = lazy(() => import("@/pages/Wedding"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const VenueHub = lazy(() => import("@/pages/VenueHub"));
const Venue = lazy(() => import("@/pages/Venue"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // The static heroes only belong on their own pages.
    const hero = document.getElementById("jh-hero");
    if (hero) hero.style.display = pathname === "/" ? "" : "none";
    const locHero = document.getElementById("jh-loc-hero");
    if (locHero) {
      const p = pathname.replace(/\/+$/, "");
      locHero.style.display = p === `/${locHero.dataset.area}-wedding-photographer` ? "" : "none";
    }
  }, [pathname]);
  return null;
};

const NotFound = () => {
  // Tell crawlers not to index unknown URLs (soft-404 guard).
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const created = !el;
    if (created) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    const prev = el.getAttribute("content");
    el.setAttribute("content", "noindex, follow");
    return () => {
      if (created) el.remove();
      else if (prev) el.setAttribute("content", prev);
    };
  }, []);
  return (
    <main className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <p className="font-serif text-7xl md:text-9xl text-ink">404</p>
      <p className="text-ink-soft mt-4 mb-8">This page has wandered off somewhere scenic.</p>
      <Link to="/" className="border border-ink text-ink text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full hover:bg-ink hover:text-paper transition-colors duration-500">
        Back home
      </Link>
    </main>
  );
};

// Legacy /wedding-photographer/:area/ URLs permanently redirect to the canonical
// /:area-wedding-photographer/ form (server 301s live in redirects.json/htaccess).
const AreaRedirect = () => {
  const { area } = useParams();
  return <Navigate to={`/${area}-wedding-photographer/`} replace />;
};

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <SmoothScroll>
          <ScrollToTop />
          <Navbar />
          <main>
            <Suspense fallback={<div className="min-h-[100svh] bg-paper" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/gallery/portfolio/" element={<Portfolio />} />
              <Route path="/gallery/portfolio" element={<Portfolio />} />
              <Route path="/gallery/:slug/" element={<Wedding />} />
              <Route path="/gallery/:slug" element={<Wedding />} />
              <Route path="/investment/" element={<Investment />} />
              <Route path="/investment" element={<Investment />} />
              <Route path="/about/" element={<About />} />
              <Route path="/about" element={<About />} />
              <Route path="/kind-words/" element={<KindWords />} />
              <Route path="/kind-words" element={<KindWords />} />
              <Route path="/contact/" element={<Contact />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy/" element={<Privacy />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms/" element={<Terms />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/wedding-photographer/:area/" element={<AreaRedirect />} />
              <Route path="/wedding-photographer/:area" element={<AreaRedirect />} />
              <Route path="/chester-wedding-photographer/" element={<Location areaSlug="chester" />} />
              <Route path="/chester-wedding-photographer" element={<Location areaSlug="chester" />} />
              <Route path="/cheshire-wedding-photographer/" element={<Location areaSlug="cheshire" />} />
              <Route path="/cheshire-wedding-photographer" element={<Location areaSlug="cheshire" />} />
              <Route path="/north-wales-wedding-photographer/" element={<Location areaSlug="north-wales" />} />
              <Route path="/north-wales-wedding-photographer" element={<Location areaSlug="north-wales" />} />
              <Route path="/liverpool-wedding-photographer/" element={<Location areaSlug="liverpool" />} />
              <Route path="/liverpool-wedding-photographer" element={<Location areaSlug="liverpool" />} />
              <Route path="/manchester-wedding-photographer/" element={<Location areaSlug="manchester" />} />
              <Route path="/manchester-wedding-photographer" element={<Location areaSlug="manchester" />} />
              <Route path="/wirral-wedding-photographer/" element={<Location areaSlug="wirral" />} />
              <Route path="/wirral-wedding-photographer" element={<Location areaSlug="wirral" />} />
              <Route path="/cheshire-wedding-venues" element={<VenueHub />} />
              <Route path="/weddings/:slug" element={<Venue />} />
              <Route path="/weddings/:slug/" element={<Venue />} />
              <Route path="/blog" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer />
            </Suspense>
          </main>
        </SmoothScroll>
      </BrowserRouter>
      <Suspense fallback={null}>
        <Toaster position="bottom-right" theme="light" toastOptions={{ style: { fontFamily: "Manrope, sans-serif" } }} />
      </Suspense>
    </div>
  );
}

export default App;
